import { useQuery } from "react-query";
import { GET_CLIENT_VIDEO_CALLS, STALE_TIME_1_MINUTES } from "@constants/reactQueryKeys";
import { getUpcomingClientVideoCalls, getPastClientVideoCalls } from "api/video";

type VideoCallParams = {
  page?: number;
  size?: number;
  sort?: 'scheduled_date' | 'billing_type' | 'call_duration';
  direction?: 'asc' | 'desc';
  get_all_client_calls?: boolean;
};

type VideoCall = {
  id: number;
  client_id: number;
  scheduled_date: string;
  start_date: string | null;
  end_date: string | null;
  call_duration: number;
  billing_type: string;
  video_key: string;
  clinician_user_id: number;
  clinician_name: string;
  clinician_avatar: string;
  client_name: string;
  client_avatar: string;
  call_timezone: string;
  canceled: boolean;
  removed: boolean;
};

type PaginatedResponse = {
  data: VideoCall[];
  pagination: {
    page: number;
    size: number;
    total: number;
    totalPages: number;
  };
};

export const useClientVideoCalls = (
  clientId: number | string,
  type: 'upcoming' | 'past',
  params: VideoCallParams = {}
): {
  videoCalls: VideoCall[] | null;
  videoCallsLoading: boolean;
  refetchVideoCalls: () => void;
  isVideoCallsRefetching: boolean;
  pagination: PaginatedResponse['pagination'] | null;
} => {
  const defaultParams = {
    page: 0,
    size: 10,
    sort: 'scheduled_date' as const,
    direction: type === 'past' ? 'desc' as const : 'asc' as const,
    ...params
  };

  const fetchFunction = type === 'upcoming' ? getUpcomingClientVideoCalls : getPastClientVideoCalls;
  
  const { data, isLoading, refetch, isRefetching } = useQuery(
    [GET_CLIENT_VIDEO_CALLS, clientId, type, defaultParams.page, defaultParams.size, defaultParams.sort, defaultParams.direction],
    () => fetchFunction(clientId, defaultParams),
    {
      enabled: Boolean(clientId),
      staleTime: STALE_TIME_1_MINUTES,
      keepPreviousData: true,
      retry: 3,
    }
  );

  return {
    videoCalls: data?.data?.data || null,
    videoCallsLoading: isLoading,
    refetchVideoCalls: refetch,
    isVideoCallsRefetching: isRefetching,
    pagination: data?.data?.pagination || null,
  };
};
